import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { LanguageSchema } from "./column-schemas.js";
/** Common stored fields of a document share returned in the user's list. */
const DocumentShareFields = S.Struct({
    id: S.UUID,
    file_id: S.UUID,
    filename_download: S.NullOr(S.String),
    type: S.NullOr(S.String),
    filesize: S.NullOr(S.String),
    folder: S.NullOr(S.UUID),
    folder_name: S.NullOr(S.String),
    folder_path: S.NullOr(S.String),
    client_id: S.NullOr(S.UUID),
    client_name: S.NullOr(S.String),
    visibility_scope: S.NullOr(S.String),
    uploaded_by: S.NullOr(S.UUID),
    uploaded_by_email: S.NullOr(S.String),
    uploaded_on: S.NullOr(S.String),
    shared_by: S.NullOr(S.UUID),
    share_date: S.NullOr(S.String),
    /**
     * The `folder_shares` row this file reaches the audience through, when it
     * does — non-null means the audience holds the parent folder, so the file
     * came with it rather than being shared on its own. The document list
     * badges those rows.
     */
    folder_share_id: S.NullOr(S.UUID),
});
const FolderIdCsvList = S.transform(S.String, S.Array(S.UUID), {
    strict: true,
    decode: (value) => value.split(",").filter((id) => id.length > 0),
    encode: (ids) => ids.join(","),
});
export const MyDocumentRow = S.extend(DocumentShareFields, S.Struct({
    /** Seeds the edit dialog's Title field; blank without it. */
    title: S.NullOr(S.String),
    translation_group_id: S.NullOr(S.Int),
    language: S.NullOr(LanguageSchema),
    uploaded_by_first_name: S.NullOr(S.String),
    uploaded_by_last_name: S.NullOr(S.String),
}));
export const ListMyDocumentsParams = S.extend(PagingParams, S.Struct({
    kind: S.Literal("mine", "client"),
    client_id: S.optional(S.UUID),
    folder_ids: S.optional(FolderIdCsvList),
    /**
     * PMO-39: `"subtree"` widens a search from the folders in `folder_ids`
     * to everything beneath them, so a search made while browsing
     * `Begehungsberichte` reaches the reports filed in its per-location
     * subfolders, and one made from the top level (where `folder_ids` is
     * the caller's own root plus the protected folder) reaches the client's
     * whole document tree.
     *
     * The subtree is resolved here rather than by the caller enumerating it:
     * a large client's tree runs into the hundreds of folders, and sending
     * those ids as a `folder_ids` CSV blew past nginx's request-line limit
     * (`414`, PMO-252).
     */
    search_scope: S.optional(S.Literal("subtree")),
    search: S.optional(S.String),
    translation_group_id: S.optional(S.NumberFromString.pipe(S.int())),
    language: S.optional(LanguageSchema),
    exclude_file_id: S.optional(S.UUID),
}));
/** The signed-in employee's file shares; identity is never caller-supplied. */
export const ListMyDocuments = HttpApiEndpoint.get("listMyDocuments", "/documents/mine")
    .setUrlParams(ListMyDocumentsParams)
    .addSuccess(pagedResponse(MyDocumentRow))
    .addError(Forbidden, { status: 403 });
export const adminDocumentsGroup = HttpApiGroup.make("admin-documents")
    .add(ListMyDocuments)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-documents.js.map